import { NavLink } from 'react-router-dom';
import { cn } from '@crestrewards/ui';
import {
  Boxes,
  Building2,
  LayoutDashboard,
  QrCode,
  ScrollText,
  ShieldCheck,
  ToggleRight,
} from 'lucide-react';

import { currentPhaseLabel } from '../constants/phases';

/**
 * Primary navigation. Collapses to an icon rail at tablet width and is hidden
 * below that, where Topbar takes over.
 *
 * The brand gradient appears here exactly once: the active item's left
 * indicator. See CLAUDE.md convention 5.
 *
 * **Grouped rather than one flat list.** Seven links in a row all look equally
 * important, so an operator has to read every label to find the one they want.
 * The groups say what kind of thing each link is — something you look at,
 * something you onboard, something you configure per client, something that
 * already happened — which is the distinction that actually matters when
 * scanning. The order within each group is unchanged.
 */
const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: 'Platform',
    items: [
      { to: '/partners', label: 'Partners', icon: Boxes },
      { to: '/clients', label: 'Clients', icon: Building2 },
    ],
  },
  {
    // Everything here is set per client rather than platform-wide, which is the
    // thing that trips people up: these screens all open on a client picker.
    label: 'Per client',
    items: [
      { to: '/security-parameters', label: 'Security parameters', icon: ShieldCheck },
      { to: '/qr-templates', label: 'QR templates', icon: QrCode },
      { to: '/features', label: 'Features', icon: ToggleRight },
    ],
  },
  {
    label: 'Records',
    items: [
      // Last on purpose: it is where you go to answer a question about something
      // that already happened, not somewhere you configure anything.
      { to: '/audit-log', label: 'Audit log', icon: ScrollText },
    ],
  },
];

export default function Sidebar({ className, onNavigate }) {
  return (
    <nav
      aria-label="Main"
      className={cn('flex h-full flex-col border-r-hairline border-border-chrome bg-card', className)}
    >
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b-hairline border-border-chrome px-4">
        {/* One of the three sanctioned homes for the brand gradient. */}
        <span
          aria-hidden="true"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-brand-gradient text-sm font-bold text-white"
        >
          C
        </span>
        <span className="truncate text-title font-semibold tracking-tight text-foreground md:hidden lg:inline">
          CrestRewards
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {NAV_GROUPS.map((group, groupIndex) => {
          const headingId = `nav-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`;

          return (
            <div key={group.label} className={groupIndex > 0 ? 'mt-4' : undefined}>
              {/*
                `muted-foreground`, not `subtle-foreground`. These are labels a
                person reads, and CLAUDE.md restricts subtle-foreground to
                disabled controls and decorative glyphs because it fails AA on
                every surface at 2.85:1.
              */}
              <p
                id={headingId}
                className="px-3 pb-1.5 text-label font-semibold uppercase tracking-wide text-muted-foreground md:hidden lg:block"
              >
                {group.label}
              </p>

              {/*
                On the icon rail there is no room for a heading, so the grouping
                survives as a short divider instead — the shape of the nav stays
                the same at both widths.
              */}
              {groupIndex > 0 ? (
                <span
                  aria-hidden="true"
                  className="mx-auto mb-2 block h-px w-6 bg-border md:block lg:hidden"
                />
              ) : null}

              <ul aria-labelledby={headingId} className="space-y-0.5">
                {group.items.map(({ to, label, icon: Icon }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      onClick={onNavigate}
                      title={label}
                      className={({ isActive }) =>
                        cn(
                          'group relative flex items-center gap-3 rounded-md px-3 py-2 text-meta transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                          // The rail has no labels, so the icon is centred
                          // rather than left-aligned against absent text.
                          'md:justify-center lg:justify-start',
                          isActive
                            ? 'bg-primary-tint font-medium text-primary-on-tint'
                            : 'text-muted-foreground hover:bg-surface-tint hover:text-primary-on-tint',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span
                            aria-hidden="true"
                            className={cn(
                              'absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full transition-opacity',
                              isActive ? 'bg-brand-gradient opacity-100' : 'opacity-0',
                            )}
                          />
                          <Icon
                            className={cn(
                              'h-4 w-4 shrink-0 transition-colors',
                              isActive ? 'text-primary' : 'text-muted-foreground',
                            )}
                            aria-hidden="true"
                          />
                          <span className="truncate md:hidden lg:inline">{label}</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="shrink-0 border-t-hairline border-border-chrome bg-surface-tint p-3 md:hidden lg:block">
        <p className="text-label font-medium text-foreground">Super Admin console</p>
        <p className="text-label text-muted-foreground">{currentPhaseLabel()}</p>
      </div>
    </nav>
  );
}
